import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import Navbar from './components/layout/Navbar';
import HomePage from './components/pages/HomePage';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import PrivateRoute from './components/auth/PrivateRoute';
import DashboardPage from './components/pages/DashboardPage';
import CoursesPage from './components/pages/CoursesPage';
import LessonPage from './components/pages/LessonPage';
import GamesPage from './components/pages/GamesPage';
import VocabRunGame from './components/games/VocabRunGame';
import WordScrambleGame from './components/games/WordScrambleGame';
import WordSearchGame from './components/games/WordSearchGame';
// 🟢 [新增] 访客试玩外壳
import GuestShell from './components/games/GuestShell';
import ProgressPage from './components/pages/ProgressPage';
import ShopPage from './components/pages/ShopPage';
import AdminPage from './components/admin/AdminPage';
import TeacherPage from './components/teacher/TeacherPage';
// 🟢 [新增] 引入 AssignmentPage
import AssignmentPage from './components/pages/AssignmentPage';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { GameProvider } from './context/GameContext';
// 🟢 [图标升级] 全局渐变定义（供 GradientIcon 引用，只渲染一次）
import { GradientDefs } from './components/common/GradientIcon';

// 🟢 [新增] 访客路由守卫。
// 只在挂载那一刻判断是否已登录：
//  - 已登录用户点到分享链接 -> 转到真正的游戏页（成绩会正常入账）
//  - 访客在弹窗里注册成功 -> user 变成非空，但这里【不会】重定向，
//    否则 setUser 触发的重渲染会立刻卸载 GuestSignupPrompt，
//    补分请求虽然还会发出去，但用户看不到 "+N points added!"，
//    还会被甩到游戏页而不是 dashboard。转化后的跳转由弹窗自己负责。
function GuestRoute({ children, loggedInTo }) {
  const { user } = useAuth();
  // AppContent 在 loading 结束后才渲染，所以挂载时 user 已经是最终值
  const [wasLoggedInAtMount] = useState(() => !!user);

  if (wasLoggedInAtMount) return <Navigate to={loggedInTo} replace />;
  return children;
}

function AppContent() {
  const { user, loading } = useAuth();
  // 🟢 2. 获取当前 location
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-white border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 pb-16 md:pb-0">
      <GradientDefs />
      <AnimatePresence mode="wait">
        {/* 🟢 3. 将 location 和 key 传递给 Routes，这样动画才能生效 */}
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* 🟢 [新增] 访客试玩（公开路由，无需登录）。
              已登录用户点到分享链接时，直接转到真正的游戏页 —— 那边的成绩会正常入账。 */}
          <Route path="/try" element={
            <GuestRoute loggedInTo="/games"><GuestShell /></GuestRoute>
          } />
          <Route path="/try/vocab-run" element={
            <GuestRoute loggedInTo="/games/vocab-run">
              <GuestShell><VocabRunGame guest /></GuestShell>
            </GuestRoute>
          } />
          <Route path="/try/word-scramble" element={
            <GuestRoute loggedInTo="/games/word-scramble">
              <GuestShell><WordScrambleGame guest /></GuestShell>
            </GuestRoute>
          } />
          <Route path="/try/word-search" element={
            <GuestRoute loggedInTo="/games/word-search">
              <GuestShell><WordSearchGame guest /></GuestShell>
            </GuestRoute>
          } />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <PrivateRoute>
              {user?.role === 'teacher' ? <Navigate to="/teacher" /> : 
               user?.role === 'admin' ? <Navigate to="/admin" /> : (
                <>
                  <Navbar />
                  <DashboardPage />
                </>
              )}
            </PrivateRoute>
          } />
          
          <Route path="/courses" element={
            <PrivateRoute allowedRoles={['student']}>
              <Navbar />
              <CoursesPage />
            </PrivateRoute>
          } />
          
          <Route path="/lesson/:lessonId" element={
            <PrivateRoute allowedRoles={['student']}>
              <Navbar />
              <LessonPage />
            </PrivateRoute>
          } />
          
          <Route path="/games" element={
            <PrivateRoute allowedRoles={['student']}>
              <Navbar />
              <GamesPage />
            </PrivateRoute>
          } />
          
          <Route path="/games/vocab-run" element={
            <PrivateRoute allowedRoles={['student']}>
              <Navbar />
              <VocabRunGame />
            </PrivateRoute>
          } />
          
          <Route path="/games/word-scramble" element={
            <PrivateRoute allowedRoles={['student']}>
              <Navbar />
              <WordScrambleGame />
            </PrivateRoute>
          } />
          
          <Route path="/games/word-search" element={
            <PrivateRoute allowedRoles={['student']}>
              <Navbar />
              <WordSearchGame />
            </PrivateRoute>
          } />
          
          <Route path="/progress" element={
            <PrivateRoute allowedRoles={['student']}>
              <Navbar />
              <ProgressPage />
            </PrivateRoute>
          } />
          
          <Route path="/shop" element={
            <PrivateRoute allowedRoles={['student']}>
              <Navbar />
              <ShopPage />
            </PrivateRoute>
          } />
          
          <Route path="/admin" element={
            <PrivateRoute allowedRoles={['admin']}>
              <Navbar />
              <AdminPage />
            </PrivateRoute>
          } />
          
          <Route path="/teacher" element={
            <PrivateRoute allowedRoles={['teacher']}>
              <Navbar />
              <TeacherPage />
            </PrivateRoute>
          } />
          
          {/* 🟢 [新增] 作业页面路由 */}
          <Route path="/assignment/:id" element={
            <PrivateRoute allowedRoles={['student']}>
              <AssignmentPage />
            </PrivateRoute>
          } />

        </Routes>
      </AnimatePresence>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <GameProvider>
        <AppContent />
      </GameProvider>
    </AuthProvider>
  );
}

export default App;
